import { Tooltip } from 'antd';
import React, { CSSProperties } from 'react';

import StatusTag from '@src/emp-3.0/components/common/StatusTag';
import Icon from '@ant-design/icons';
import createdSvg from '@icons/emp/completed.svg';
import readyToCreateSvg from '@icons/emp/ready_to_create.svg';

interface IStatusTagRender {
  type?:
    | 'blocked'
    | 'undeployed'
    | 'new'
    | 'rename-new'
    | 'conviva-predefined'
    | 'is_defined'
    | 'undefined'
    | 'created'
    | 'ready';
  style?: CSSProperties;
}

interface IResponsiveStatusTag extends IStatusTagRender {
  text: string;
  tooltip?: string;
  alwaysShowTooltip?: boolean;
  currentScreenWidth?: number;
  limitScreenWidth?: number;
}

const SCREEN_WIDTH_THRESHOLD = 1600;

export const StatusTagRender: React.FC<IStatusTagRender> = ({ type, style, children }) => {
  const commonStyle = { padding: '2px 4px', lineHeight: '16px' };

  switch (type) {
    case 'blocked':
      return (
        <StatusTag
          style={{
            ...commonStyle,
            opacity: 0.9,
            color: '#ffffff',
            background: 'rgba(0, 0, 0, 0.45)',
            ...style,
          }}
        >
          {children}
        </StatusTag>
      );
    case 'undeployed':
      return (
        <StatusTag
          style={{
            ...commonStyle,
            color: '#ad4e00',
            background: '#ffe7ba',
            ...style,
          }}
        >
          {children}
        </StatusTag>
      );
    case 'new':
      return (
        <StatusTag type="primary" typeColor="blue" style={{ ...commonStyle, ...style }}>
          {children}
        </StatusTag>
      );
    case 'rename-new':
      return (
        <StatusTag type="primary" typeColor="green" style={{ ...commonStyle, ...style }}>
          {children}
        </StatusTag>
      );
    case 'conviva-predefined':
      return (
        <StatusTag
          type="ghost"
          typeColor="gray"
          style={{
            ...commonStyle,
            height: '22px',
            border: '1px solid #c9c9c9',
            ...style,
          }}
        >
          {children}
        </StatusTag>
      );
    case 'is_defined':
      return (
        <StatusTag
          type="primary"
          typeColor="black"
          style={{ height: '22px', paddingInline: '7px', ...style }}
        >
          {children ?? 'Defined'}
        </StatusTag>
      );
    case 'undefined':
      return (
        <span style={{ color: 'rgba(0, 0, 0, 0.45)', ...style }}>{children ?? 'Undefined'}</span>
      );
    case 'created':
      return (
        <Tooltip title="Created">
          <Icon component={createdSvg} style={{ fontSize: 16, ...style }} />
        </Tooltip>
      );
    case 'ready':
      return (
        <Tooltip title="Ready To Create">
          <Icon component={readyToCreateSvg} style={{ fontSize: 16, ...style }} />
        </Tooltip>
      );
    default:
      return (
        <StatusTag
          style={{
            ...commonStyle,
            color: '#949494',
            background: '#f7f7f7',
            ...style,
          }}
        >
          {children}
        </StatusTag>
      );
  }
};

const ResponsiveStatusTag = ({
  text,
  tooltip,
  alwaysShowTooltip,
  currentScreenWidth,
  type,
  limitScreenWidth = SCREEN_WIDTH_THRESHOLD,
  style = {},
}: IResponsiveStatusTag) => {
  const shouldAbbreviateText = currentScreenWidth && currentScreenWidth < limitScreenWidth;
  if (alwaysShowTooltip || shouldAbbreviateText) {
    const displayText = shouldAbbreviateText ? text.slice(0, 1) : text;
    return (
      <Tooltip title={tooltip ?? text}>
        <StatusTagRender style={{ marginRight: '5px', fontWeight: 500, ...style }} type={type}>
          {displayText}
        </StatusTagRender>
      </Tooltip>
    );
  }
  return (
    <StatusTagRender type={type} style={style}>
      {text}
    </StatusTagRender>
  );
};

export default ResponsiveStatusTag;
